import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { FiArrowLeft, FiRefreshCw } from 'react-icons/fi';
import useAuthStore from '../stores/authStore';
import { motionReviewApi, readableError } from './api';
import { getAdminReviewSelection } from './adminReviewSections';
import { createAdminReviewQueue } from './adminReviewQueue';
import { createMotionAdminSaveQueue } from './motionAdminSaveQueue';
import { createMotionMediaBuffer } from './motionMediaBuffer';
import { MotionEvidence, MotionViewer, useCompactMotionView } from './MotionReviewScreen';
import MotionAnswerPanel from './MotionAnswerPanel';
import './community.css';
import './adminMotionReview.css';

const MAX_PENDING_SAVES = 20;
const isTyping = (target) => Boolean(target?.closest?.('input, textarea, select, [contenteditable]:not([contenteditable="false"]), .cr-motion-rules, .cr-motion-mobile-tools, video[controls]'));
const outcomeName = (outcome) => ({ animal: 'Real motion', false_alarm: 'False alarm', unsure: 'Not sure' }[outcome] || outcome);

function StaffReviewWorkspace({ api, selection }) {
  const compact = useCompactMotionView();
  const [queue] = useState(() => createAdminReviewQueue({ api, ...selection }));
  const [snapshot, setSnapshot] = useState(() => queue.getSnapshot());
  const [saveQueue] = useState(() => createMotionAdminSaveQueue(api));
  const [, renderSaves] = useState(0);
  const [reviewedCount, setReviewedCount] = useState(0);
  const [loadedFor, setLoadedFor] = useState(null);
  const [failedFor, setFailedFor] = useState(null);
  const [enlarged, setEnlarged] = useState(false);
  const [refreshingClip, setRefreshingClip] = useState(false);
  const [refreshVersion, setRefreshVersion] = useState(0);
  const [scanPaused, setScanPaused] = useState(false);
  const [error, setError] = useState('');
  const [, renderMedia] = useState(0);
  const mounted = useRef(false);
  const disposalTimer = useRef(null);
  const refillPages = useRef(0);
  const previous = useRef(null);
  const current = useRef(null);
  const actionLocked = useRef(null);
  const mediaWarmed = useRef(() => {});
  const [media] = useState(() => createMotionMediaBuffer({
    maxTasks: 6, warmCount: 6, concurrency: 2, preloadEnabled: false,
    onWarm: () => mediaWarmed.current(),
  }));
  mediaWarmed.current = () => { if (mounted.current) renderMedia((value) => value + 1); };
  const task = snapshot.tasks[0] || null;
  current.current = task;
  const presentation = task ? `${task.id}-${refreshVersion}` : null;
  const loaded = loadedFor === presentation;
  const failed = failedFor === presentation;
  const pending = saveQueue.pendingCount;
  const failures = saveQueue.failures;
  const backpressure = pending + failures.length >= MAX_PENDING_SAVES;

  useEffect(() => {
    mounted.current = true;
    window.clearTimeout(disposalTimer.current);
    const unsubscribe = queue.subscribe(setSnapshot);
    setSnapshot(queue.getSnapshot());
    queue.load();
    return () => {
      mounted.current = false;
      unsubscribe();
      // StrictMode remounts effects in development; only dispose on departure.
      disposalTimer.current = window.setTimeout(() => { queue.dispose(); media.dispose(); }, 0);
    };
  }, [media, queue]);
  useEffect(() => saveQueue.subscribe(() => {
    // This view removes answered clips itself, so completed save overlays can
    // be released once writes settle instead of accumulating for the session.
    if (!saveQueue.pendingCount) saveQueue.acceptRefresh(saveQueue.version);
    renderSaves((value) => value + 1);
  }), [saveQueue]);
  useEffect(() => {
    const beforeUnload = (event) => {
      if (saveQueue.pendingCount || saveQueue.failures.length) { event.preventDefault(); event.returnValue = ''; }
    };
    window.addEventListener('beforeunload', beforeUnload);
    return () => window.removeEventListener('beforeunload', beforeUnload);
  }, [saveQueue]);
  useEffect(() => {
    if (snapshot.tasks.length > 5) { refillPages.current = 0; return; }
    if (snapshot.loading || snapshot.error || snapshot.exhausted || scanPaused) return;
    // A large run of cleaned-up clips must not cause an unbounded scan.
    if (refillPages.current >= 12) { setScanPaused(true); return; }
    refillPages.current += 1;
    queue.load();
  }, [queue, scanPaused, snapshot]);
  useEffect(() => {
    media.syncTasks(snapshot.tasks.slice(0, 6), { includeMasks: !compact, previousTasks: previous.current ? [previous.current] : [] });
  }, [compact, media, snapshot.tasks]);
  useEffect(() => {
    if (!task) return undefined;
    const timer = window.setTimeout(() => media.setPreloadEnabled(true), 1000);
    return () => window.clearTimeout(timer);
  }, [media, task]);
  useEffect(() => {
    if (!task || loaded || failed || refreshingClip) return undefined;
    const timer = window.setTimeout(() => setFailedFor(presentation), 20000);
    return () => window.clearTimeout(timer);
  }, [failed, loaded, presentation, refreshingClip, task]);

  // Keep the public player and its two warm mobile decoders. The staff queue
  // never touches the volunteer outbox, progress, reservations, or submissions.
  const preparedTask = task ? media.readyTask(task) : null;
  const upcoming = compact ? snapshot.tasks.slice(1, 3).map((item) => media.decoderTask(item)).filter(Boolean) : [];
  const disabled = !task || !loaded || failed || refreshingClip || backpressure;

  const advance = useCallback((selected) => {
    previous.current = selected;
    refillPages.current = 0;
    setScanPaused(false);
    setEnlarged(false);
    setError('');
    queue.remove(selected.id);
    if (!compact) window.scrollTo?.({ top: 0, behavior: 'instant' });
  }, [compact, queue]);
  const decide = useCallback((outcome, flagAction = null) => {
    const selected = current.current;
    if (!selected || disabled || enlarged || actionLocked.current === selected.id) return;
    // Unlock by track identity, so a second event cannot answer the old clip
    // even if React has not committed the next presentation yet.
    actionLocked.current = selected.id;
    if (outcome === 'unsure') { advance(selected); return; }
    saveQueue.enqueue('resolve', { taskId: selected.id, outcome, speciesId: null,
      ...(flagAction === 'keep' ? { keepFlags: true } : {}),
      ...(flagAction === 'dismiss' ? { dismissFlags: true } : {}) }, selected);
    setReviewedCount((value) => value + 1);
    advance(selected);
  }, [advance, disabled, enlarged, saveQueue]);
  const skip = useCallback(() => {
    if (!current.current || refreshingClip || actionLocked.current === current.current.id || enlarged) return;
    actionLocked.current = current.current.id;
    advance(current.current);
  }, [advance, enlarged, refreshingClip]);
  const dismissFlags = () => {
    const selected = current.current;
    if (!selected || disabled || enlarged || actionLocked.current === selected.id) return;
    actionLocked.current = selected.id;
    saveQueue.enqueue('dismiss', { taskId: selected.id }, selected);
    advance(selected);
  };
  const refreshClip = useCallback(async () => {
    const selected = current.current;
    if (!selected || refreshingClip || actionLocked.current === selected.id) return;
    setRefreshingClip(true); setEnlarged(false); setError('');
    try {
      const refreshed = await queue.refreshTask(selected.id);
      if (!mounted.current) return;
      media.forgetPresentation(selected.id);
      renderMedia((value) => value + 1);
      setRefreshVersion((value) => value + 1);
      if (!refreshed) setError('This clip is no longer available in this section.');
    } catch (requestError) { if (mounted.current) setError(readableError(requestError)); }
    finally { if (mounted.current) setRefreshingClip(false); }
  }, [media, queue, refreshingClip]);
  const loadMore = () => {
    refillPages.current = 0; setScanPaused(false); setError('');
    if (snapshot.exhausted || snapshot.error?.code === 'review/pagination-loop') queue.refresh();
    else if (snapshot.error && snapshot.tasks.length > 5) refreshClip();
    else queue.load();
  };
  const closeEnlarged = useCallback(() => setEnlarged(false), []);
  useEffect(() => {
    const keyDown = (event) => {
      if (event.defaultPrevented || event.repeat || event.isComposing || event.ctrlKey || event.metaKey || event.altKey || event.shiftKey || isTyping(event.target) || enlarged) return;
      const key = event.code === 'Space' || event.key === 'Spacebar' ? ' ' : event.key?.toLowerCase();
      if (key === ' ' && event.target?.closest?.('button, a, summary')) return;
      if (!disabled && ['s', 'n', ' '].includes(key)) {
        event.preventDefault();
        decide(key === 's' ? 'animal' : key === 'n' ? 'false_alarm' : 'unsure');
      }
    };
    document.addEventListener('keydown', keyDown);
    return () => document.removeEventListener('keydown', keyDown);
  }, [decide, disabled, enlarged]);

  const flags = task?.flags || task?.flagCounts || {};
  const activeFlags = task && !task.flagsDismissed && !task.flagsKept && (task.hasFlags || flags.predator > 0 || flags.interesting > 0);
  return <div className="community-app cr-staff-review-app">
    <header className="cr-header"><div className="cr-brand"><span>Ting Vision</span><span className="cr-brand-divider" aria-hidden="true" /><span className="cr-brand-subtitle">Staff review</span></div>
      <a className="cr-staff-back" href="/admin/community-reviews"><FiArrowLeft aria-hidden="true" /> Back to reviews</a>
    </header>
    <main className="cr-main cr-motion-main cr-staff-review-main">
      <div className="cr-intro"><h1>Review mode</h1><details className="cr-motion-rules"><summary>Review rules and shortcuts</summary><div className="cr-motion-rules-body"><ul>
        <li><span>Animal or bird moving on the ground or in a tree, even outside the box?</span><span className="cr-motion-rule-key">Real motion · <kbd>S</kbd></span></li>
        <li><span>Flying birds, moving plants or water?</span><span className="cr-motion-rule-key">False alarm · <kbd>N</kbd></span></li>
        <li><span>Unsure? Skip without changing the decision.</span><span className="cr-motion-rule-key">Not sure · <kbd>Space</kbd></span></li>
      </ul></div></details></div>
      <div className="cr-staff-review-summary"><h2>{selection.title}</h2><span>{reviewedCount} reviewed this session{pending ? ` · ${pending} saving…` : ''}</span></div>
      <p className="cr-small cr-muted cr-staff-review-finality">Only tracks from this section. Real motion and False alarm save final staff decisions. Not sure skips.</p>
      {failures.map((failure) => <div className="cr-error cr-staff-review-save-error" role="alert" key={`${failure.payload.taskId}-${failure.kind}`}>
        <span>Couldn’t save track {failure.payload.taskId.slice(0, 8)}: {readableError(failure.error)}</span>
        <button className="cr-text-button" type="button" onClick={() => saveQueue.retry(failure)}>Retry save</button>
      </div>)}
      {backpressure && <p className="cr-error" role="status">Your decisions still need to save. Retry any failed saves or wait a moment before reviewing more.</p>}
      {(snapshot.error || error) && <p className="cr-error" role="alert">{error || readableError(snapshot.error)} {snapshot.error && <button type="button" className="cr-text-button" onClick={loadMore} disabled={snapshot.loading}>Retry loading this section</button>}</p>}
      {!task ? <div className="cr-empty" role={snapshot.loading ? 'status' : undefined}>
        <h2>{snapshot.loading ? 'Finding the next clip in this section…' : snapshot.error ? 'We couldn’t load this section' : scanPaused ? 'No playable clips found in this batch' : 'You’re caught up in this section'}</h2>
        <p>{snapshot.loading ? 'Preparing your review clips.' : 'Only this section is searched. Other community clips will not be loaded.'}</p>
        {!snapshot.loading && <button className="cr-primary" onClick={loadMore}><FiRefreshCw aria-hidden="true" />{scanPaused ? 'Continue searching this section' : snapshot.error ? 'Try again' : 'Check this section again'}</button>}
      </div> : <div className="cr-review-layout">
        <section className="cr-photo-section" aria-label="Motion evidence">
          <MotionEvidence key={compact ? `staff-mobile-${refreshVersion}` : presentation} task={preparedTask} active={!enlarged} upcomingTasks={upcoming} presentationKey={presentation} showSpottedTime
            onLoad={() => { setLoadedFor(presentation); setFailedFor(null); media.setPreloadEnabled(true); }}
            onError={() => { setFailedFor(presentation); media.setPreloadEnabled(true); }}
            onRefresh={refreshClip} onEnlarge={() => setEnlarged(true)} />
          <p className="cr-motion-hint">Count any animal or bird moving on the ground or in a tree, including outside the highlighted box. The clip repeats so you can take another look.</p>
          {refreshingClip ? <p className="cr-small cr-muted" role="status">Refreshing the clip…</p> : !loaded && !failed && <p className="cr-small cr-muted" role="status">Loading the clip…</p>}
          {failed && <p className="cr-error" role="alert">This clip couldn’t load. <button className="cr-text-button" onClick={refreshClip} disabled={refreshingClip}>Refresh clip</button> or <button className="cr-text-button" onClick={skip}>Skip clip</button>.</p>}
        </section>
        <MotionAnswerPanel>
          <h2 className="cr-motion-question">Is there real motion?</h2>
          {task.status === 'closed' && <p className="cr-small cr-muted cr-staff-current-decision">{task.resolutionSource === 'community' ? 'Community' : 'Current decision'}: {outcomeName(task.outcome)}</p>}
          {selection.filter === 'flagged' && activeFlags && <fieldset className="cr-motion-flags" disabled={disabled}><legend>Review this flag</legend>
            <button className="cr-flag-action" onClick={() => decide('animal', 'keep')}>Real &amp; keep</button>
            <button className="cr-flag-action" onClick={() => decide('animal', 'dismiss')}>Real &amp; dismiss</button>
            <button className="cr-flag-action" onClick={() => decide('false_alarm', 'dismiss')}>False &amp; dismiss</button>
            <button className="cr-flag-action" onClick={dismissFlags}>Dismiss flags</button>
          </fieldset>}
          <div className="cr-motion-submit" role="group" aria-label="Staff motion review controls">
            <div className="cr-motion-verdicts" role="group" aria-label="Motion review answer">
              {[['animal', 'Real motion', 'S'], ['false_alarm', 'False alarm', 'N'], ['unsure', 'Not sure', 'Space']].map(([outcome, label, shortcut]) => <button type="button" className={`cr-verdict${outcome === 'animal' ? ' cr-motion-animal' : ''}`} key={outcome} disabled={disabled} aria-keyshortcuts={shortcut} onClick={() => decide(outcome)}>{label}<kbd aria-hidden="true">{shortcut}</kbd></button>)}
            </div>
            <div className="cr-staff-secondary-actions"><button type="button" className="cr-verdict" disabled={refreshingClip} onClick={skip}>Skip clip</button><button type="button" className="cr-verdict cr-staff-remove" disabled={disabled} onClick={() => decide('removed')}>Remove track</button></div>
          </div>
        </MotionAnswerPanel>
      </div>}
      {enlarged && preparedTask && <MotionViewer task={preparedTask} onClose={closeEnlarged} onRefresh={refreshClip} />}
    </main>
  </div>;
}

export default function AdminMotionReviewScreen({ api = motionReviewApi }) {
  const { search } = useLocation();
  const role = useAuthStore((state) => state.role);
  const accessLoading = useAuthStore((state) => state.isAccessLoading);
  const uid = useAuthStore((state) => state.user?.uid);
  const selection = useMemo(() => getAdminReviewSelection(search), [search]);
  if (accessLoading) return <div className="community-app"><main className="cr-empty" role="status">Checking staff access…</main></div>;
  if (role !== 'admin') return <div className="community-app"><main className="cr-empty"><h1>Staff access required</h1><p>This page is available to Ting Vision administrators.</p></main></div>;
  if (!selection) return <div className="community-app"><main className="cr-empty"><h1>Choose a review section</h1><p>Open Review mode from the section you want to check.</p><a href="/admin/community-reviews">Back to community reviews</a></main></div>;
  return <StaffReviewWorkspace key={`${uid}-${selection.filter}-${selection.autoFlaggedDirection}`} api={api} selection={selection} />;
}
